/**
 * Resource Services
 * Collection-specific service methods for all API resources
 * Re-exports from split modules for backward compatibility
 */

import { API_CONFIG } from '../config/api.config';
import { ResourceService } from './resources.service.base';
import {
  currenciesServiceExtended,
  invoicesServiceExtended,
  employeesServiceExtended,
  productsServiceExtended,
} from './resources.service.extended';

// Re-export types
export type { ResourceQueryOptions } from './resources.service.base';

// Core Business Resources
export const clientsService = new ResourceService(API_CONFIG.ENDPOINTS.CLIENTS);
export const providersService = new ResourceService(API_CONFIG.ENDPOINTS.PROVIDERS);
export const branchesService = new ResourceService(API_CONFIG.ENDPOINTS.BRANCHES);
export const employeesService = new ResourceService(API_CONFIG.ENDPOINTS.EMPLOYEES);
export const employeeDocumentsService = new ResourceService(API_CONFIG.ENDPOINTS.EMPLOYEE_DOCUMENTS);

// Financial Resources
export const invoicesService = new ResourceService(API_CONFIG.ENDPOINTS.INVOICES);
export const invoiceItemsService = new ResourceService(API_CONFIG.ENDPOINTS.INVOICE_ITEMS);
export const debitNotesService = new ResourceService(API_CONFIG.ENDPOINTS.DEBIT_NOTES);
export const deductionsService = new ResourceService(API_CONFIG.ENDPOINTS.DEDUCTIONS);
export const purchasesService = new ResourceService(API_CONFIG.ENDPOINTS.PURCHASES);
export const purchaseItemsService = new ResourceService(API_CONFIG.ENDPOINTS.PURCHASE_ITEMS);
export const billsService = new ResourceService(API_CONFIG.ENDPOINTS.BILLS);
export const paymentOrdersService = new ResourceService(API_CONFIG.ENDPOINTS.PAYMENT_ORDERS);
export const paymentOrderItemsService = new ResourceService(API_CONFIG.ENDPOINTS.PAYMENT_ORDER_ITEMS);
export const transactionsService = new ResourceService(API_CONFIG.ENDPOINTS.TRANSACTIONS);
export const pettyCashesService = new ResourceService(API_CONFIG.ENDPOINTS.PETTY_CASHES);
export const pettyCashExpensesService = new ResourceService(API_CONFIG.ENDPOINTS.PETTY_CASH_EXPENSES);

// Banking Resources
export const banksService = new ResourceService(API_CONFIG.ENDPOINTS.BANKS);
export const safesService = new ResourceService(API_CONFIG.ENDPOINTS.SAFES);
export const categoriesService = new ResourceService(API_CONFIG.ENDPOINTS.CATEGORIES);
export const currenciesService = new ResourceService(API_CONFIG.ENDPOINTS.CURRENCIES);

// Contracts & FOA
export const contractsService = new ResourceService(API_CONFIG.ENDPOINTS.CONTRACTS);
export const foasService = new ResourceService(API_CONFIG.ENDPOINTS.FOAS);

// Crew & HR Resources
export const ranksService = new ResourceService(API_CONFIG.ENDPOINTS.RANKS);
export const crewCertificatesService = new ResourceService(API_CONFIG.ENDPOINTS.CREW_CERTIFICATES);
export const crewChangesService = new ResourceService(API_CONFIG.ENDPOINTS.CREW_CHANGES);
export const crewListsService = new ResourceService(API_CONFIG.ENDPOINTS.CREW_LISTS);
export const crewEvaluationsService = new ResourceService(API_CONFIG.ENDPOINTS.CREW_EVALUATIONS);

// Recruitment Resources
export const candidatesService = new ResourceService(API_CONFIG.ENDPOINTS.CANDIDATES);
export const candidateRatingsService = new ResourceService(API_CONFIG.ENDPOINTS.CANDIDATE_RATINGS);
export const assessmentPointsService = new ResourceService(API_CONFIG.ENDPOINTS.ASSESSMENT_POINTS);
export const careersService = new ResourceService(API_CONFIG.ENDPOINTS.CAREERS);

// Fleet Management Resources
export const carsService = new ResourceService(API_CONFIG.ENDPOINTS.CARS);
export const carDocumentsService = new ResourceService(API_CONFIG.ENDPOINTS.CAR_DOCUMENTS);
export const carPermitsService = new ResourceService(API_CONFIG.ENDPOINTS.CAR_PERMITS);
export const personnelPermitsService = new ResourceService(API_CONFIG.ENDPOINTS.PERSONNEL_PERMITS);

// Requisitions & Delivery Resources
export const codePrefixesService = new ResourceService(API_CONFIG.ENDPOINTS.CODE_PREFIXES);
export const requisitionsService = new ResourceService(API_CONFIG.ENDPOINTS.REQUISITIONS);
export const requisitionItemsService = new ResourceService(API_CONFIG.ENDPOINTS.REQUISITION_ITEMS);
export const deliveryNotesService = new ResourceService(API_CONFIG.ENDPOINTS.DELIVERY_NOTES);
export const deliveryNoteItemsService = new ResourceService(API_CONFIG.ENDPOINTS.DELIVERY_NOTE_ITEMS);

// Invoice Hire Resources
export const invoiceHiresService = new ResourceService(API_CONFIG.ENDPOINTS.INVOICE_HIRES);
export const invoiceHireItemsService = new ResourceService(API_CONFIG.ENDPOINTS.INVOICE_HIRE_ITEMS);

// Inventory Resources
export const inventoryCategoriesService = new ResourceService(API_CONFIG.ENDPOINTS.INVENTORY_CATEGORIES);
export const productsService = new ResourceService(API_CONFIG.ENDPOINTS.PRODUCTS);

// System Resources
export const rolesService = new ResourceService(API_CONFIG.ENDPOINTS.ROLES);
export const systemUsersService = new ResourceService(API_CONFIG.ENDPOINTS.SYSTEM_USERS);
export const permissionsService = new ResourceService(API_CONFIG.ENDPOINTS.PERMISSIONS);
export const locationsService = new ResourceService(API_CONFIG.ENDPOINTS.LOCATIONS);
export const ownersService = new ResourceService(API_CONFIG.ENDPOINTS.OWNERS);
export const documentTypesService = new ResourceService(API_CONFIG.ENDPOINTS.DOCUMENT_TYPES);

// Website Resources
export const serviceItemsService = new ResourceService(API_CONFIG.ENDPOINTS.SERVICE_ITEMS);
export const fleetVesselsService = new ResourceService(API_CONFIG.ENDPOINTS.FLEET_VESSELS);
export const contactOfficesService = new ResourceService(API_CONFIG.ENDPOINTS.CONTACT_OFFICES);

// Letters & Reports Resources
export const permitLettersService = new ResourceService(API_CONFIG.ENDPOINTS.PERMIT_LETTERS);
export const letterGenerationsService = new ResourceService(API_CONFIG.ENDPOINTS.LETTER_GENERATIONS);
export const financeReportsService = new ResourceService(API_CONFIG.ENDPOINTS.FINANCE_REPORTS);

const allServices = {
  // Core
  clients: clientsService,
  providers: providersService,
  branches: branchesService,
  employees: employeesServiceExtended,
  employeeDocuments: employeeDocumentsService,

  // Financial
  invoices: invoicesServiceExtended,
  invoiceItems: invoiceItemsService,
  debitNotes: debitNotesService,
  deductions: deductionsService,
  purchases: purchasesService,
  purchaseItems: purchaseItemsService,
  bills: billsService,
  paymentOrders: paymentOrdersService,
  paymentOrderItems: paymentOrderItemsService,
  transactions: transactionsService,
  pettyCashes: pettyCashesService,
  pettyCashExpenses: pettyCashExpensesService,

  // Banking
  banks: banksService,
  safes: safesService,
  categories: categoriesService,
  currencies: currenciesServiceExtended,

  // Contracts
  contracts: contractsService,
  foas: foasService,

  // Crew & HR
  ranks: ranksService,
  crewCertificates: crewCertificatesService,
  crewChanges: crewChangesService,
  crewLists: crewListsService,
  crewEvaluations: crewEvaluationsService,

  // Recruitment
  candidates: candidatesService,
  candidateRatings: candidateRatingsService,
  assessmentPoints: assessmentPointsService,
  careers: careersService,

  // Fleet
  cars: carsService,
  carDocuments: carDocumentsService,
  carPermits: carPermitsService,
  personnelPermits: personnelPermitsService,

  // Requisitions
  codePrefixes: codePrefixesService,
  requisitions: requisitionsService,
  requisitionItems: requisitionItemsService,
  deliveryNotes: deliveryNotesService,
  deliveryNoteItems: deliveryNoteItemsService,

  // Invoice Hire
  invoiceHires: invoiceHiresService,
  invoiceHireItems: invoiceHireItemsService,

  // Inventory
  inventoryCategories: inventoryCategoriesService,
  products: productsServiceExtended,

  // System
  roles: rolesService,
  systemUsers: systemUsersService,
  permissions: permissionsService,
  locations: locationsService,
  owners: ownersService,
  documentTypes: documentTypesService,

  // Website
  serviceItems: serviceItemsService,
  fleetVessels: fleetVesselsService,
  contactOffices: contactOfficesService,

  // Letters & Reports
  permitLetters: permitLettersService,
  letterGenerations: letterGenerationsService,
  financeReports: financeReportsService,
};

export default allServices;
